// Kinio 即時影像 signaling（docs/KINIO-API.md §4）。
//
// `wss://<plane api host>/v1/media/signal`，subprotocol `kinio-signal-v1`，一則 JSON 一個 TEXT frame。
// 101 之後 **10 秒內**要送第一幀 `{"type":"signin",access_token,home_id}`（不走 header / query），
// 伺服器回 `signin_ok` 或 `signin_error{reason}`。之後：
//   → {"type":"command","request_id","device_ref","command":"start_viewing|stop_viewing","payload":{…}}
//   ← {"type":"response","request_id","command","status":"ok|<設備錯誤碼，例 HARDWARE_ERROR / INVALID_PAYLOAD>","payload":{…}}
//   ↔ {"type":"ice_candidate","session_id","device_ref","candidate":{…}|null}   （雙向 trickle；null = 收集完成）
//   ← {"type":"error","request_id?","session_id?","code","message","retry_after_ms"?}   code = UPPER_SNAKE（FORBIDDEN / DEVICE_OFFLINE / VIEWER_LIMIT / TIMEOUT…）
//
// 一條連線服務整個家戶（多個 session 用 session_id 區分）。WebSocket 不受 CORS 管，
// 所以一律直打 `homes[].endpoints.api` 的 host，不走 vite dev proxy。
//
// 媒體 lease 續租（plan vms-system §5.1；platform 95f7c86）：這條連線上所有 session 的租期 = signin 用的 access token 的 exp。
//   ← signin_ok.renew_supported:true、token_exp
//   ← {"type":"lease_expiring","token_exp","expires_in_seconds"}                 到期前 60 秒提示一次
//   → {"type":"renew","request_id","access_token"}                        換一顆新 token 續租（同 user / realm / identity session）
//   ← {"type":"response","request_id","command":"renew","status":"ok","payload":{"token_exp"}} | error{request_id, code}
//   ← {"type":"error","session_id","device_ref","code":"TOKEN_EXPIRED"} + close 1008 "token_expired"   到期沒續：停流、關線
// 我們在 token_exp - 60 s 自己排一次 renew（保險，不只靠 lease_expiring）；過期被關線 → 安靜重連（重新 signin），
// LiveView 收到 TOKEN_EXPIRED 會自己重開觀看，不顯示錯誤。

import { refresh, session } from './auth';
import { KinioError } from './errors';
import { LEASE_RENEW_RETRY_MS } from './lease';

export { LEASE_RENEW_RETRY_MS };

export const SIGNIN_TIMEOUT_MS = 10_000;
export const COMMAND_TIMEOUT_MS = 30_000;
/** token 剩餘不到這個時間就先 refresh 再 signin / renew。 */
export const TOKEN_RENEW_MARGIN_MS = 60_000;
/** 租期（token_exp）前多久自己續租（伺服器 lease_expiring 也是 60 秒前）。 */
export const LEASE_RENEW_LEAD_MS = 60_000;

export type SignalState = 'closed' | 'connecting' | 'open';

export interface SignalFrame {
  type: string;
  [k: string]: unknown;
}

export interface SignalResponse extends SignalFrame {
  type: 'response';
  request_id?: string;
  command?: string;
  status?: string;
  payload?: Record<string, unknown>;
}

export interface IceCandidateFrame extends SignalFrame {
  type: 'ice_candidate';
  session_id?: string;
  device_ref?: string;
  /** null = 對端 gathering 完成。 */
  candidate: RTCIceCandidateInit | null;
}

export interface SignalErrorFrame extends SignalFrame {
  type: 'error';
  request_id?: string;
  session_id?: string;
  code?: string;
  message?: string;
  retry_after_ms?: number;
}

export interface LeaseExpiringFrame extends SignalFrame {
  type: 'lease_expiring';
  token_exp?: number;
  expires_in_seconds?: number;
}

type Events = {
  state: SignalState;
  lease_expiring: LeaseExpiringFrame;
  /** 續租成功（新的 token_exp，unix 秒）。 */
  renewed: number;
  ice_candidate: IceCandidateFrame;
  error: SignalErrorFrame;
  frame: SignalFrame;
};

type Handler<K extends keyof Events> = (v: Events[K]) => void;

/** `https://api.<plane 主機>` → `wss://api.<plane 主機>/v1/media/signal`。 */
export function signalUrl(apiEndpoint: string): string {
  const base = apiEndpoint.trim().replace(/\/+$/, '');
  try {
    const u = new URL(base);
    const scheme = u.protocol === 'http:' ? 'ws:' : 'wss:';
    return `${scheme}//${u.host}${u.pathname.replace(/\/+$/, '')}/v1/media/signal`;
  } catch {
    return base.replace(/^http/, 'ws') + '/v1/media/signal';
  }
}

let seq = 0;
export function newRequestId(): string {
  seq += 1;
  return `req-${Date.now().toString(36)}-${seq}`;
}

export interface MediaSignalOptions {
  /** 測試注入（預設 globalThis.WebSocket）。 */
  WebSocketImpl?: typeof WebSocket;
  signinTimeoutMs?: number;
  commandTimeoutMs?: number;
  /** 非預期斷線時自動重連（預設 true）。 */
  autoReconnect?: boolean;
  /** 重連 backoff（ms）；用完取最後一個。 */
  backoffMs?: number[];
  /** 測試注入。 */
  setTimeoutImpl?: typeof setTimeout;
}

/**
 * 伺服端關線的原因 → 錯誤碼（media hub recheck，vms-system §5.1）：1008 `session revoked`（身分 session 被撤：登出其他裝置、
 * 重設密碼）→ SESSION_REVOKED、1008 `access revoked`（撤權 / 離職）→ ACCESS_REVOKED——這兩種重連也沒用（signin 還是過，
 * 下一輪 recheck 又被關），呼叫端當終態；1008 `token_expired` → TOKEN_EXPIRED（refresh 後可重來）；其他 → SIGNAL_CLOSED。
 */
export function closeErrorCode(c: { code: number; reason: string } | null | undefined): string {
  if (c?.code === 1008) {
    const r = c.reason.toLowerCase();
    if (r.includes('session revoked')) return 'SESSION_REVOKED';
    if (r.includes('access revoked')) return 'ACCESS_REVOKED';
    if (r.includes('token_expired')) return 'TOKEN_EXPIRED';
  }
  return 'SIGNAL_CLOSED';
}

/** 媒體信令的 WebSocket subprotocol（wire 常數；平台 internal/media HandleWS）。 */
export const SIGNAL_SUBPROTOCOL = 'kinio-signal-v1';

/** 伺服器宣告這條連線的租期已過：error 幀 code `TOKEN_EXPIRED`。 */
export function isTokenExpired(code: unknown): boolean {
  return code === 'TOKEN_EXPIRED';
}

/** close 1008 的 reason `token_expired`（session 結束原因，lower_snake）。 */
export function isTokenExpiredClose(reason: unknown): boolean {
  return reason === 'token_expired';
}

interface Pending {
  resolve: (r: SignalResponse) => void;
  reject: (e: unknown) => void;
  timer: ReturnType<typeof setTimeout>;
}

const DEFAULT_BACKOFF = [500, 1000, 2000, 4000, 8000, 15_000];

/**
 * 一個家戶一條 signaling 連線。`ready()` 冪等：沒連就連、連了就直接 resolve。
 * `close()` 之後可以再 `ready()`（重新開一條）。
 */
export class MediaSignal {
  readonly apiEndpoint: string;
  readonly homeId: string;
  private readonly opts: Required<Omit<MediaSignalOptions, 'WebSocketImpl' | 'setTimeoutImpl'>> & {
    WebSocketImpl: typeof WebSocket;
    setTimeoutImpl: typeof setTimeout;
  };
  private ws: WebSocket | null = null;
  private _state: SignalState = 'closed';
  private wanted = false;
  private opening: Promise<void> | null = null;
  private pending = new Map<string, Pending>();
  private listeners: { [K in keyof Events]: Set<Handler<K>> } = {
    state: new Set(),
    lease_expiring: new Set(),
    renewed: new Set(),
    ice_candidate: new Set(),
    error: new Set(),
    frame: new Set(),
  };
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  /** signin_ok 帶回的資訊（診斷用）。 */
  lastSignin: { user_id?: string; client_conn_id?: string; token_exp?: number; renew_supported?: boolean } | null = null;
  /** 這次斷線是租期到了（安靜重連中）：LiveView 也安靜重開觀看。 */
  get quietReconnect(): boolean {
    return this.leaseExpired;
  }
  /** 最近一次斷線的 close code / reason（LiveView 用 closeErrorCode 判斷要不要重連）。 */
  lastClose: { code: number; reason: string } | null = null;
  /** 伺服器支援 renew（signin_ok.renew_supported）。不支援 = 舊伺服器，到期前只能重連。 */
  renewSupported = false;
  /** 目前租期（unix 秒；0 = 未知）。 */
  tokenExp = 0;
  /** 這條連線 signin / 最近一次 renew 用的 access token。 */
  private connToken: string | null = null;
  private renewTimer: ReturnType<typeof setTimeout> | null = null;
  private renewing: Promise<number> | null = null;
  /** 下一次斷線是租期到了（安靜重連，不算失敗、不退避）。 */
  private leaseExpired = false;

  constructor(apiEndpoint: string, homeId: string, options: MediaSignalOptions = {}) {
    this.apiEndpoint = apiEndpoint;
    this.homeId = homeId;
    this.opts = {
      WebSocketImpl: options.WebSocketImpl ?? (globalThis.WebSocket as typeof WebSocket),
      signinTimeoutMs: options.signinTimeoutMs ?? SIGNIN_TIMEOUT_MS,
      commandTimeoutMs: options.commandTimeoutMs ?? COMMAND_TIMEOUT_MS,
      autoReconnect: options.autoReconnect ?? true,
      backoffMs: options.backoffMs ?? DEFAULT_BACKOFF,
      // 包一層：直接存 `setTimeout` 再以 `this.opts.setTimeoutImpl(…)` 呼叫，
      // 在瀏覽器會變成「以 opts 當 this 呼叫 window.setTimeout」→ Illegal invocation。
      setTimeoutImpl: options.setTimeoutImpl ?? (((fn: () => void, ms: number) => setTimeout(fn, ms)) as unknown as typeof setTimeout),
    };
  }

  get state(): SignalState {
    return this._state;
  }

  get url(): string {
    return signalUrl(this.apiEndpoint);
  }

  on<K extends keyof Events>(type: K, fn: Handler<K>): () => void {
    this.listeners[type].add(fn as never);
    return () => {
      this.listeners[type].delete(fn as never);
    };
  }

  private emit<K extends keyof Events>(type: K, v: Events[K]): void {
    for (const fn of Array.from(this.listeners[type]) as Handler<K>[]) {
      try {
        fn(v);
      } catch {
        /* 訂閱者自己的錯不影響 wire */
      }
    }
  }

  private setState(s: SignalState): void {
    if (this._state === s) return;
    this._state = s;
    this.emit('state', s);
  }

  /** 連線 + signin（冪等、單飛）。resolve 代表已收到 signin_ok。 */
  ready(): Promise<void> {
    this.wanted = true;
    if (this._state === 'open') return Promise.resolve();
    if (this.opening) return this.opening;
    this.opening = this.open().finally(() => {
      this.opening = null;
    });
    return this.opening;
  }

  private async token(): Promise<string> {
    const cur = session.get();
    if (!cur) throw new KinioError(401, 'NO_SESSION', 'not signed in');
    // 快到期（或已過期，例如租期到了重連）就先換一顆，免得連上去立刻被踢。
    if (cur.expires_at > 0 && cur.expires_at - Date.now() < TOKEN_RENEW_MARGIN_MS) {
      try {
        return await refresh();
      } catch {
        /* refresh 失敗仍用舊的試一次 */
      }
    }
    const tok = session.accessToken();
    if (!tok) throw new KinioError(401, 'NO_SESSION', 'not signed in');
    return tok;
  }

  private open(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      void (async () => {
        let accessToken: string;
        try {
          accessToken = await this.token();
          this.connToken = accessToken;
        } catch (e) {
          this.setState('closed');
          reject(e);
          return;
        }
        const WS = this.opts.WebSocketImpl;
        if (!WS) {
          this.setState('closed');
          reject(new KinioError(0, 'NO_WEBSOCKET', 'WebSocket unavailable'));
          return;
        }
        this.setState('connecting');
        let ws: WebSocket;
        try {
          ws = new WS(this.url, SIGNAL_SUBPROTOCOL);
        } catch (e) {
          this.setState('closed');
          reject(KinioError.network(e));
          return;
        }
        this.ws = ws;
        let settled = false;
        const timer = this.opts.setTimeoutImpl(() => {
          if (settled) return;
          settled = true;
          try {
            ws.close();
          } catch {
            /* already closed */
          }
          reject(new KinioError(408, 'SIGNIN_TIMEOUT', 'media signal signin timed out'));
        }, this.opts.signinTimeoutMs);

        const fail = (e: KinioError) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          reject(e);
        };

        ws.onopen = () => {
          // 101 後 10 秒內第一幀必須是 signin（不走 header / query）。
          try {
            ws.send(JSON.stringify({ type: 'signin', access_token: accessToken, home_id: this.homeId }));
          } catch (e) {
            fail(KinioError.network(e));
          }
        };

        ws.onmessage = (ev: MessageEvent) => {
          let frame: SignalFrame;
          try {
            frame = JSON.parse(typeof ev.data === 'string' ? ev.data : String(ev.data)) as SignalFrame;
          } catch {
            return;
          }
          if (!settled) {
            if (frame.type === 'signin_ok') {
              settled = true;
              clearTimeout(timer);
              this.reconnectAttempt = 0;
              this.lastSignin = {
                user_id: typeof frame.user_id === 'string' ? frame.user_id : undefined,
                client_conn_id: typeof frame.client_conn_id === 'string' ? frame.client_conn_id : undefined,
                token_exp: typeof frame.token_exp === 'number' ? frame.token_exp : undefined,
                renew_supported: frame.renew_supported === true,
              };
              this.renewSupported = frame.renew_supported === true;
              this.tokenExp = typeof frame.token_exp === 'number' ? frame.token_exp : 0;
              this.leaseExpired = false;
              this.scheduleRenew();
              this.setState('open');
              this.emit('frame', frame);
              resolve();
              return;
            }
            if (frame.type === 'signin_error') {
              const reason = typeof frame.reason === 'string' ? frame.reason : 'SIGNIN_ERROR';
              // UNAUTHORIZED 是終局（別重連轟炸）。
              this.wanted = false;
              fail(new KinioError(reason === 'UNAUTHORIZED' ? 401 : 403, reason, `media signal signin: ${reason}`));
              try {
                ws.close();
              } catch {
                /* already closed */
              }
              return;
            }
          }
          this.dispatch(frame);
        };

        ws.onerror = () => {
          fail(KinioError.network(new Error('media signal socket error')));
        };

        ws.onclose = (ev?: CloseEvent) => {
          if (this.ws !== ws && this.ws !== null) return; // 已被新連線取代（例如租期到了我們先關掉重連）
          this.ws = null;
          this.clearRenew();
          this.lastClose = { code: ev?.code ?? 0, reason: ev?.reason ?? '' };
          // 1008 "token_expired"：租期到了被伺服器關線 → 安靜重連（下一次 signin 會先換新 token），不退避。
          if (ev && ev.code === 1008 && isTokenExpiredClose(ev.reason)) this.leaseExpired = true;
          // 被撤權 / 身分 session 被撤：自動重連只會 signin 成功、下一輪 recheck 又被關（W17 第二輪 2026-09-27 每 ~15 秒一次）
          const why = closeErrorCode(this.lastClose);
          if (why === 'SESSION_REVOKED' || why === 'ACCESS_REVOKED') this.wanted = false;
          fail(new KinioError(0, 'SIGNAL_CLOSED', 'media signal closed before signin'));
          this.setState('closed');
          this.rejectAllPending(new KinioError(0, 'SIGNAL_CLOSED', 'media signal closed'));
          this.scheduleReconnect();
        };
      })();
    });
  }

  private dispatch(frame: SignalFrame): void {
    this.emit('frame', frame);
    switch (frame.type) {
      case 'response': {
        const rid = typeof frame.request_id === 'string' ? frame.request_id : '';
        const p = this.pending.get(rid);
        if (p) {
          this.pending.delete(rid);
          clearTimeout(p.timer);
          p.resolve(frame as SignalResponse);
        }
        return;
      }
      case 'ice_candidate':
        this.emit('ice_candidate', frame as IceCandidateFrame);
        return;
      case 'lease_expiring': {
        const f = frame as LeaseExpiringFrame;
        this.emit('lease_expiring', f);
        // 已經續到更晚的租期（自排的 renew 先到）→ 不重送
        if (typeof f.token_exp === 'number' && this.tokenExp > f.token_exp) return;
        void this.renew().catch(() => {
          /* renew 內部會排重試；到期沒續 → TOKEN_EXPIRED → 安靜重連 */
        });
        return;
      }
      case 'error': {
        const err = frame as SignalErrorFrame;
        const rid = typeof err.request_id === 'string' ? err.request_id : '';
        if (!rid && isTokenExpired(err.code)) {
          // 租期到了：伺服器接著會關線（1008）。先標記成安靜重連，別讓新的 start_viewing 打在這條要死的連線上。
          this.leaseExpired = true;
          this.dropSocket();
        }
        const p = rid ? this.pending.get(rid) : undefined;
        if (p) {
          this.pending.delete(rid);
          clearTimeout(p.timer);
          p.reject(signalError(err));
        }
        // 沒有 request_id 的 error（例如觀看中設備掉線）要推給訂閱者。
        this.emit('error', err);
        return;
      }
      default:
        return;
    }
  }

  private rejectAllPending(e: unknown): void {
    for (const [, p] of this.pending) {
      clearTimeout(p.timer);
      p.reject(e);
    }
    this.pending.clear();
  }

  /** 我們自己關掉目前的 socket（租期到了）：狀態立刻變 closed，之後的 ready() 開新連線。 */
  private dropSocket(): void {
    const ws = this.ws;
    if (!ws) return;
    this.ws = null;
    this.clearRenew();
    this.setState('closed');
    this.rejectAllPending(new KinioError(0, 'SIGNAL_CLOSED', 'media signal lease expired'));
    try {
      ws.close();
    } catch {
      /* already closed */
    }
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (!this.opts.autoReconnect || !this.wanted || this.reconnectTimer) return;
    const b = this.opts.backoffMs;
    const quiet = this.leaseExpired;
    const delay = quiet ? 0 : (b[Math.min(this.reconnectAttempt, b.length - 1)] ?? 15_000);
    if (!quiet) this.reconnectAttempt += 1;
    this.reconnectTimer = this.opts.setTimeoutImpl(() => {
      this.reconnectTimer = null;
      if (!this.wanted || this._state !== 'closed') return;
      void this.ready().catch(() => {
        /* 下一輪 onclose 會再排 */
      });
    }, delay);
  }

  private clearRenew(): void {
    if (this.renewTimer) {
      clearTimeout(this.renewTimer);
      this.renewTimer = null;
    }
  }

  /** 在 token_exp - LEASE_RENEW_LEAD_MS 自己續租（保險；lease_expiring 也會觸發）。伺服器不支援 renew → 不排。 */
  private scheduleRenew(delayMs?: number): void {
    this.clearRenew();
    if (!this.renewSupported || !this.tokenExp) return;
    const due = delayMs ?? Math.max(0, this.tokenExp * 1000 - LEASE_RENEW_LEAD_MS - Date.now());
    this.renewTimer = this.opts.setTimeoutImpl(() => {
      this.renewTimer = null;
      if (this._state !== 'open') return;
      void this.renew().catch(() => {
        /* renew 內部排重試 */
      });
    }, due);
  }

  /** 續租要用的 token：session 裡已經有一顆比連線那顆新、而且還夠久的就直接用，否則 refresh（單飛）。 */
  private async renewToken(): Promise<string> {
    const cur = session.get();
    if (!cur) throw new KinioError(401, 'NO_SESSION', 'not signed in');
    if (cur.access_token !== this.connToken && cur.expires_at - Date.now() > TOKEN_RENEW_MARGIN_MS) return cur.access_token;
    return refresh();
  }

  /**
   * 續租：refresh identity token → 同一條連線送 `{type:"renew", request_id, access_token}`，用 request_id 對回覆。
   * 成功 → 新的 token_exp（unix 秒），並重排下一次；失敗 → 租期內 LEASE_RENEW_RETRY_MS 後再試，拋出錯誤。單飛。
   */
  renew(): Promise<number> {
    if (this.renewing) return this.renewing;
    this.renewing = (async () => {
      if (this._state !== 'open' || !this.ws) throw new KinioError(0, 'SIGNAL_CLOSED', 'media signal not open');
      try {
        const tok = await this.renewToken();
        const requestId = newRequestId();
        const r = await this.sendAndWait({ type: 'renew', request_id: requestId, access_token: tok }, requestId, 'renew');
        const exp = typeof r.payload?.token_exp === 'number' ? r.payload.token_exp : 0;
        this.connToken = tok;
        if (exp) this.tokenExp = exp;
        this.scheduleRenew();
        this.emit('renewed', this.tokenExp);
        return this.tokenExp;
      } catch (e) {
        // forbidden（換了人 / 家戶權限被收 / 不同 identity session）重試也不會過 → 不排；其他（unauthorized、
        // authorization_unavailable、逾時、refresh 失敗）租期內再試。續不上 → 到期 token_expired → 安靜重連。
        const hopeless = e instanceof KinioError && e.code === 'FORBIDDEN';
        if (!hopeless && this._state === 'open' && this.tokenExp * 1000 > Date.now()) this.scheduleRenew(LEASE_RENEW_RETRY_MS);
        throw e;
      }
    })().finally(() => {
      this.renewing = null;
    });
    return this.renewing;
  }

  /** 舊名（VMS / stream 同名）：續租，失敗不拋（renew 內部已排重試）。 */
  renewLease(): Promise<void> {
    return this.renew().then(
      () => undefined,
      () => undefined,
    );
  }

  /** 送一幀並等對應 request_id 的 response（status ≠ ok 或 error frame → 拋 KinioError）。 */
  private sendAndWait(frame: Record<string, unknown>, requestId: string, label: string, timeoutMs?: number): Promise<SignalResponse> {
    const ws = this.ws;
    if (!ws || this._state !== 'open') return Promise.reject(new KinioError(0, 'SIGNAL_CLOSED', 'media signal not open'));
    return new Promise<SignalResponse>((resolve, reject) => {
      const timer = this.opts.setTimeoutImpl(() => {
        this.pending.delete(requestId);
        reject(new KinioError(408, 'COMMAND_TIMEOUT', `${label} timed out`));
      }, timeoutMs ?? this.opts.commandTimeoutMs);
      this.pending.set(requestId, {
        timer,
        reject,
        resolve: (r) => {
          const status = typeof r.status === 'string' ? r.status : 'ok';
          if (status !== 'ok') {
            // 設備回應碼 UPPER_SNAKE；REJECTED 的原因（例 privacy_mode）在 reason（payload / detail / 頂層，平台 hub 轉過來）
            reject(new KinioError(409, status, `${label}: ${status}`, responseDetail(r)));
            return;
          }
          resolve(r);
        },
      });
      try {
        ws.send(JSON.stringify(frame));
      } catch (e) {
        this.pending.delete(requestId);
        clearTimeout(timer);
        reject(KinioError.network(e));
      }
    });
  }

  /**
   * 送一個 `command` 並等對應 `request_id` 的 `response`。
   * `status !== 'ok'` → 拋 `KinioError(status)`；`error` frame → 拋帶 code 的 KinioError。
   */
  async request(deviceRef: string, command: string, payload: Record<string, unknown> = {}, timeoutMs?: number): Promise<SignalResponse> {
    await this.ready();
    if (!this.ws || this._state !== 'open') throw new KinioError(0, 'SIGNAL_CLOSED', 'media signal not open');
    const requestId = newRequestId();
    return this.sendAndWait({ type: 'command', request_id: requestId, device_ref: deviceRef, command, payload }, requestId, command, timeoutMs);
  }

  /** trickle：本地 ICE candidate 推給設備（`candidate: null` = 收集完成）。 */
  sendIce(deviceRef: string, sessionId: string | null, candidate: RTCIceCandidateInit | null): void {
    const ws = this.ws;
    if (!ws || this._state !== 'open') return;
    try {
      ws.send(JSON.stringify({ type: 'ice_candidate', session_id: sessionId ?? undefined, device_ref: deviceRef, candidate }));
    } catch {
      /* 斷線中：candidate 丟掉即可（remote 會用已送出的） */
    }
  }

  /** 關閉連線（不再自動重連）。 */
  close(): void {
    this.wanted = false;
    this.clearRenew();
    this.leaseExpired = false;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.rejectAllPending(new KinioError(0, 'SIGNAL_CLOSED', 'media signal closed'));
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      try {
        ws.close();
      } catch {
        /* already closed */
      }
    }
    this.setState('closed');
  }
}

export function signalError(f: SignalErrorFrame): KinioError {
  const code = (typeof f.code === 'string' && f.code) || 'SIGNAL_ERROR';
  const e = new KinioError(409, code, typeof f.message === 'string' ? f.message : code, responseDetail(f));
  return e;
}

/** 失敗回應 / error frame 的補充欄位：`detail` 物件 + `reason`（payload.reason > detail.reason > 頂層 reason）。 */
export function responseDetail(f: SignalFrame): Record<string, unknown> {
  const obj = (v: unknown) => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {});
  const detail = obj(f.detail);
  const payload = obj(f.payload);
  const reason = [payload.reason, detail.reason, f.reason].find((x) => typeof x === 'string' && x);
  return reason ? { ...detail, reason } : detail;
}

// ---- 一個家戶一條共用連線（多個 live session / 多格 grid 共用） ----

const shared = new Map<string, MediaSignal>();

export function sharedSignal(apiEndpoint: string, homeId: string): MediaSignal {
  const key = `${apiEndpoint}|${homeId}`;
  let s = shared.get(key);
  if (!s) {
    s = new MediaSignal(apiEndpoint, homeId);
    shared.set(key, s);
  }
  return s;
}

/** 測試 / 登出時清掉共用連線。 */
export function closeSharedSignals(): void {
  for (const [, s] of shared) s.close();
  shared.clear();
}
